import { useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { Check, LoaderCircle, Play } from "lucide-react";
import {
  DEFAULT_PROSPECTION_BRIEF,
  PROSPECTION_LIMITS,
  STEP_LABELS,
  WEEKDAY_LABELS,
  WRITER_MODELS,
  type ProspectionSettings,
  type ProspectionStep,
  type RunRecord,
} from "@shared/lib/prospection/shared";
import { formatDateTime } from "@shared/lib/format";
import { AdminChip, AdminEmpty, AdminMeta, AdminPanel } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Checkbox } from "@shared/components/ui/checkbox";
import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { richSignatureHtml } from "@shared/lib/prospection/signature";
import { Switch } from "@shared/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { Textarea } from "@shared/components/ui/textarea";

/**
 * Réglages de la prospection. Tous les leviers vivent en base : couper les
 * envois ou changer une fenêtre ne demande aucun redéploiement, et les bornes
 * sont tenues par la base elle-même.
 */

const FOLLOWUP_OPTIONS = [
  { value: "0", label: "Aucune relance" },
  { value: "1", label: "1 relance — un autre signal à J+3" },
  { value: "2", label: "2 relances — puis encore un signal vers J+10" },
  { value: "3", label: "3 relances — puis la clôture vers J+17" },
];

function Field({
  id,
  label,
  hint,
  wide = false,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={wide ? "grid content-start gap-2 md:col-span-2" : "grid content-start gap-2"}>
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="m-0 text-[12px] leading-relaxed text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Toggle({
  id,
  label,
  hint,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-3">
      <Switch checked={checked} className="mt-0.5" id={id} onCheckedChange={onChange} />
      <div>
        <Label htmlFor={id}>{label}</Label>
        <p className="m-0 mt-1 text-[12px] leading-relaxed text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

export function SettingsView({
  settings,
  runs,
  senderName,
}: {
  settings: ProspectionSettings;
  runs: RunRecord[];
  senderName: string | null;
}) {
  const router = useRouter();
  const [form, setForm] = useState({
    ...settings,
    bounceRate: String(settings.maxBounceRateBps / 100),
  });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => setForm((current) => ({ ...current, [key]: value }));
  const number = (key: keyof typeof PROSPECTION_LIMITS) => ({
    id: `setting-${key}`,
    min: PROSPECTION_LIMITS[key].min,
    max: PROSPECTION_LIMITS[key].max,
    type: "number" as const,
    value: String(form[key]),
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => set(key, Number(event.target.value)),
  });

  async function request(key: string, path: string, method: string, body?: unknown) {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      const response = await fetch(path, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error((data as { error?: string }).error || "Opération impossible.");
      return data as { report?: { message: string }; status?: string };
    } finally {
      setBusy("");
    }
  }

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (form.sendingEnabled && !settings.sendingEnabled) {
      if (!confirm("Activer les envois ? Les e-mails prêts partiront depuis les boîtes Gmail, dans la fenêtre d’envoi.")) return;
    }
    try {
      const { bounceRate, ...rest } = form;
      await request("save", "/api/admin/prospection/settings", "PUT", {
        ...rest,
        maxBounceRateBps: Math.round(Number(bounceRate.replace(",", ".")) * 100),
      });
      setNotice("Réglages enregistrés.");
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Enregistrement impossible.");
    }
  }

  async function run(step: ProspectionStep) {
    try {
      const data = await request(`run-${step}`, "/api/admin/prospection/run", "POST", { step });
      setNotice(
        data.report?.message ??
          "Préparation lancée en arrière-plan : comptez quelques minutes par dossier, le journal se met à jour au fil des passages.",
      );
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Passage impossible.");
    }
  }

  return (
    <form className="grid gap-4.5" onSubmit={save}>
      <AdminMeta
        note={
          <Button disabled={busy === "save"} size="sm" type="submit">
            {busy === "save" ? <LoaderCircle className="animate-spin" /> : <Check />} Enregistrer les réglages
          </Button>
        }
      >
        <span className="flex flex-wrap gap-x-4 gap-y-1.5">
          <AdminChip tone={settings.machineEnabled ? "active" : "neutral"}>Préparation {settings.machineEnabled ? "active" : "coupée"}</AdminChip>
          <AdminChip tone={settings.sendingEnabled ? "active" : "warning"}>Envois {settings.sendingEnabled ? "actifs" : "coupés"}</AdminChip>
        </span>
      </AdminMeta>

      {error && (
        <Alert role="alert" variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert className="bg-muted">
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      <AdminPanel title="Interrupteurs">
        <div className="grid gap-5 md:grid-cols-2">
          <Toggle
            checked={form.machineEnabled}
            hint="Le cron prépare les relances dues et de nouveaux dossiers : offre et clientèle analysées, signaux trouvés, premier e-mail rédigé et relu."
            id="setting-machine"
            label="Préparation des dossiers"
            onChange={(value) => set("machineEnabled", value)}
          />
          <Toggle
            checked={form.sendingEnabled}
            hint="Interrupteur général. Coupé, rien ne part depuis les boîtes Gmail, relances et réponses comprises."
            id="setting-sending"
            label="Envois par Gmail"
            onChange={(value) => set("sendingEnabled", value)}
          />
          <Toggle
            checked={form.autoApprove}
            hint="Un e-mail qui passe les deux passes du garde-fou part à son créneau. Coupé, chacun attend votre clic « Valider »."
            id="setting-approve"
            label="Validation automatique"
            onChange={(value) => set("autoApprove", value)}
          />
          <Toggle
            checked={form.autoReplyYes}
            hint="Un « oui » net (confiance du modèle d’au moins 85 %) reçoit aussitôt la source et les autres signaux. Les questions attendent toujours une personne."
            id="setting-yes"
            label="Réponse automatique aux « oui »"
            onChange={(value) => set("autoReplyYes", value)}
          />
          <Toggle
            checked={form.warmupContinuous}
            hint="Après la cinquième semaine, chaque boîte garde 3 échanges par jour avec nos autres boîtes, en plus de la prospection. Coupé, la chauffe s’arrête une fois la boîte au plafond."
            id="setting-warmup-continuous"
            label="Chauffe continue"
            onChange={(value) => set("warmupContinuous", value)}
          />
        </div>
      </AdminPanel>

      <AdminPanel title="Préparation">
        <div className="grid gap-4 md:grid-cols-2">
          <Field hint="Chaque dossier lance une analyse complète du prospect (quelques minutes)." id="setting-dailyQuota" label="Dossiers par 24 heures">
            <Input {...number("dailyQuota")} />
          </Field>
          <Field hint="Une même personne, même sous une autre fiche, ne reçoit pas de nouvelle séquence avant ce délai." id="setting-cooldownDays" label="Délai avant de reprendre une personne (jours)">
            <Input {...number("cooldownDays")} />
          </Field>
        </div>
      </AdminPanel>

      <AdminPanel title="Envoi">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-2 md:col-span-2">
            <span className="text-sm font-medium">Jours d’envoi</span>
            <div className="flex flex-wrap gap-4">
              {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                <label className="flex items-center gap-2 text-[13px]" htmlFor={`day-${day}`} key={day}>
                  <Checkbox
                    checked={form.sendDays.includes(day)}
                    id={`day-${day}`}
                    onCheckedChange={(checked) =>
                      set("sendDays", checked ? [...form.sendDays, day].sort() : form.sendDays.filter((value) => value !== day))
                    }
                  />
                  {WEEKDAY_LABELS[day]}
                </label>
              ))}
            </div>
            <p className="m-0 text-[12px] text-muted-foreground">Les jours fériés sont toujours sautés. Heure de Paris.</p>
          </div>
          <Field id="setting-window-start" label="Début de la fenêtre">
            <Input id="setting-window-start" onChange={(event) => set("sendWindowStart", event.target.value)} type="time" value={form.sendWindowStart} />
          </Field>
          <Field id="setting-window-end" label="Fin de la fenêtre">
            <Input id="setting-window-end" onChange={(event) => set("sendWindowEnd", event.target.value)} type="time" value={form.sendWindowEnd} />
          </Field>
          <Field
            hint="Une boîte neuve ne fait que chauffer pendant ces jours-là, puis commence à 5 e-mails par jour, 10 la semaine suivante, 20, puis le plafond. 14 jours conseillés ; 0 pour prospecter dès le premier jour."
            id="setting-warmupDays"
            label="Durée de la chauffe (jours)"
          >
            <Input {...number("warmupDays")} />
          </Field>
          <Field hint="Atteint trois semaines après la chauffe. Pour écrire à plus de monde, ajoutez une boîte." id="setting-dailyCapPerMailbox" label="Plafond par boîte et par jour">
            <Input {...number("dailyCapPerMailbox")} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field id="setting-minGapMinutes" label="Écart minimal (min)">
              <Input {...number("minGapMinutes")} />
            </Field>
            <Field id="setting-maxGapMinutes" label="Écart maximal (min)">
              <Input {...number("maxGapMinutes")} />
            </Field>
          </div>
        </div>
      </AdminPanel>

      <AdminPanel title="Séquence et lecture des boîtes">
        <div className="grid gap-4 md:grid-cols-2">
          <Field hint="Toujours dans le même fil, arrêtée à la première réponse, désinscription ou rebond." id="setting-followups" label="Relances après le premier e-mail">
            <Select onValueChange={(value) => set("followups", Number(value))} value={String(form.followups)}>
              <SelectTrigger className="w-full" id="setting-followups">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FOLLOWUP_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field id="setting-inboxIntervalMinutes" label="Lire chaque boîte toutes les (min)">
            <Input {...number("inboxIntervalMinutes")} />
          </Field>
          <Field id="setting-inbox-start" label="Lecture à partir de">
            <Input id="setting-inbox-start" onChange={(event) => set("inboxStart", event.target.value)} type="time" value={form.inboxStart} />
          </Field>
          <Field id="setting-inbox-end" label="Lecture jusqu’à">
            <Input id="setting-inbox-end" onChange={(event) => set("inboxEnd", event.target.value)} type="time" value={form.inboxEnd} />
          </Field>
        </div>
      </AdminPanel>

      <AdminPanel title="Arrêts automatiques d’une boîte (7 jours glissants)">
        <div className="grid gap-4 md:grid-cols-2">
          <Field hint="Au-delà, la boîte passe en pause. Sous 20 envois, trois rebonds suffisent." id="setting-bounce" label="Taux de rebond maximal (%)">
            <Input id="setting-bounce" max={10} min={0.5} onChange={(event) => set("bounceRate", event.target.value)} step={0.5} type="number" value={form.bounceRate} />
          </Field>
          <Field id="setting-maxUnsubscribesPer100" label="Désinscriptions maximales pour 100 e-mails">
            <Input {...number("maxUnsubscribesPer100")} />
          </Field>
        </div>
      </AdminPanel>

      <AdminPanel title="Identité, brief et rédaction">
        <div className="grid gap-4 md:grid-cols-2">
          <Field
            hint="Écrit le premier e-mail et les relances qui livrent un autre signal, une fois le dossier rassemblé (offre, signal, interlocuteur). La relecture du garde-fou garde le modèle de l’agent."
            id="setting-writer-model"
            label="Modèle de rédaction des e-mails"
            wide
          >
            <Select onValueChange={(value) => set("writerModel", value as ProspectionSettings["writerModel"])} value={form.writerModel}>
              <SelectTrigger className="w-full md:w-1/2" id="setting-writer-model">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WRITER_MODELS.map((model) => (
                  <SelectItem key={model.value} value={model.value}>{model.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field
            hint={
              form.signatureRich
                ? "Sous le nom de la boîte, dans chaque e-mail : sa fonction, par exemple « Développement commercial, Hune »."
                : "Sous le prénom de la boîte, dans le premier e-mail. Texte brut, une ligne."
            }
            id="setting-signature"
            label="Signature"
          >
            <Input id="setting-signature" maxLength={300} onChange={(event) => set("signature", event.target.value)} value={form.signature} />
          </Field>
          <div className="grid content-start gap-3 md:col-span-2">
            <Toggle
              checked={form.signatureRich}
              hint="Comme dans Gmail : le logo Hune, le nom de la boîte, la ligne ci-dessus, le lien vers le site et la promesse, sous chaque e-mail, relances et réponses comprises. Le corps du message reste sans lien."
              id="setting-signature-rich"
              label="Signature avec logo et lien"
              onChange={(value) => set("signatureRich", value)}
            />
            {form.signatureRich && (
              <div
                aria-label="Aperçu de la signature"
                className="rounded-lg border px-5 py-4"
                // Gabarit du code, nom et ligne échappés (lib/prospection/signature.ts).
                dangerouslySetInnerHTML={{ __html: richSignatureHtml(senderName ?? "Nom de la boîte", form.signature.trim() || "Fonction") }}
              />
            )}
          </div>
          <Field
            hint="Qui écrit, ce que fait Hune, la promesse. La structure de l’e-mail et la question finale sont imposées par le code. Vide : le brief par défaut."
            id="setting-brief"
            label="Brief d’envoi"
            wide
          >
            <Textarea id="setting-brief" maxLength={4000} onChange={(event) => set("brief", event.target.value)} placeholder={DEFAULT_PROSPECTION_BRIEF} rows={6} value={form.brief} />
          </Field>
        </div>
      </AdminPanel>

      <AdminPanel
        action={
          <span className="flex flex-wrap gap-2">
            {(["prepare", "send", "inbox"] as const).map((step) => (
              <Button disabled={busy === `run-${step}`} key={step} onClick={() => run(step)} size="sm" type="button" variant="outline">
                {busy === `run-${step}` ? <LoaderCircle className="animate-spin" /> : <Play />} {STEP_LABELS[step]}
              </Button>
            ))}
          </span>
        }
        flush
        title="Passages"
      >
        {runs.length === 0 ? (
          <AdminEmpty>Aucun passage journalisé pour l’instant.</AdminEmpty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Début</TableHead>
                <TableHead>Étape</TableHead>
                <TableHead className="max-sm:pr-4">Issue</TableHead>
                <TableHead className="pr-5 max-sm:hidden">Compte rendu</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="pl-5 whitespace-nowrap tabular-nums">{formatDateTime(item.started_at)}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {STEP_LABELS[item.step]}
                    {item.source === "manual" && <small className="block text-[12px] text-muted-foreground">lancé à la main</small>}
                  </TableCell>
                  <TableCell className="max-sm:pr-4">
                    <AdminChip tone={item.status === "done" ? "active" : "danger"}>{item.status === "done" ? "Terminé" : "Échec"}</AdminChip>
                  </TableCell>
                  <TableCell className="pr-5 text-[12.5px] max-sm:hidden">
                    {item.message}
                    {item.error && <small className="block text-[12px] text-destructive">{item.error}</small>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <p className="m-0 border-t px-4 py-3 text-[12px] leading-relaxed text-muted-foreground">
          Lancer une étape à la main applique les mêmes règles que le cron : quota, exclusions, interrupteur des envois,
          capacité des boîtes. Seule la fenêtre horaire est ignorée pour l’envoi.
        </p>
      </AdminPanel>
    </form>
  );
}
